import { useState, useEffect } from 'react';
import Swal from 'sweetalert2';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import ApiService from '@/services/api';
import IncomeStatementTable from './table';

const IncomeStatement = () => {
  const [report, setReport] = useState(null);
  const [cooperative, setCooperative] = useState(null);
  const [loading, setLoading] = useState(true);
  const [filters, setFilters] = useState({
    startDate: dayjs().startOf('month').format('YYYY-MM-DD'),
    endDate: dayjs().format('YYYY-MM-DD'),
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const profile = JSON.parse(localStorage.getItem('user_profile'));
      const cooperativeId = profile?.cooperative.id || 0;
      
      setCooperative(profile?.cooperative);

      const response = await ApiService.getIncomeStatement(
        cooperativeId, 
        filters.startDate,
        filters.endDate
      );

      if (response.status === 200) {
        setReport(response.data);
      }
    } catch (error) {
      console.error('Error loading income statement:', error);
      Swal.fire({
        icon: 'error',
        title: 'Oops...',
        text: error.response?.data?.message || 'Gagal memuat laporan laba rugi!',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleFilter = () => {
    loadData();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <>
      <style>{`
        @media print {
          nav, .card-header, .no-print { 
            display: none !important; 
          }
          thead { 
            background: #1e3a8a !important; 
            -webkit-print-color-adjust: exact; 
          }
        }
      `}</style>

      <nav className="flex mb-6 no-print" aria-label="Breadcrumb">
        <ol className="inline-flex items-center space-x-1 md:space-x-3">
          <li className="inline-flex items-center">
            <Link
              to="/dashboard"
              className="inline-flex items-center text-sm font-medium text-gray-700 hover:text-blue-600"
            >
              <svg className="w-4 h-4 mr-2" fill="currentColor" viewBox="0 0 20 20">
                <path d="M10.707 2.293a1 1 0 00-1.414 0l-7 7a1 1 0 001.414 1.414L4 10.414V17a1 1 0 001 1h2a1 1 0 001-1v-2a1 1 0 011-1h2a1 1 0 011 1v2a1 1 0 001 1h2a1 1 0 001-1v-6.586l.293.293a1 1 0 001.414-1.414l-7-7z"></path>
              </svg>
              Dashboard
            </Link>
          </li>
          <li>
            <div className="flex items-center">
              <svg className="w-6 h-6 text-gray-400" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd"></path>
              </svg>
              <span className="ml-1 text-sm font-medium text-gray-700 md:ml-2">
                Laporan Laba Rugi
              </span>
            </div>
          </li>
        </ol>
      </nav>

      <div className="flex flex-auto flex-col">
        <div className="card">
          <div className="card-header no-print">
            <div className="flex justify-between items-center">
              <h4 className="card-title">Laporan Laba Rugi</h4>
              <div className="flex gap-2">
                <input
                  type="date"
                  className="form-input w-full"
                  value={filters.startDate}
                  onChange={(e) => setFilters({ ...filters, startDate: e.target.value })}
                />
                <input
                  type="date"
                  className="form-input w-full"
                  value={filters.endDate}
                  onChange={(e) => setFilters({ ...filters, endDate: e.target.value })}
                />
                <button
                  className="btn  bg-blue-600 text-white px-4 py-2 rounded"
                  onClick={handleFilter}
                  disabled={loading}
                >
                  <i className="mgc_filter_line me-2"></i>
                  Filter
                </button>
                <button
                  className="btn btn-sm bg-secondary text-white"
                  onClick={loadData}
                  disabled={loading}
                >
                  <i className="mgc_refresh_1_line me-2"></i>Refresh
                </button>
                <button
                  className="btn btn-smbtn-sm bg-success text-white"
                  onClick={handlePrint}
                >
                  <i className="mgc_printer_line me-2"></i>Print
                </button>
              </div>
            </div>
          </div>

          {/* Filter Section */}
          <div className="p-6 border-b no-print">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              
            </div>
          </div>

          {/* Report Content */}
          <div className="p-6">
            {loading ? (
              <div className="flex justify-center items-center py-8">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
              </div>
            ) : (
              <>
                {/* Cooperative Header for Print */}
                {cooperative && (
                  <div className="text-center mb-6">
                    <h2 className="text-xl font-bold">{cooperative.name}</h2>
                    {/* <p className="text-sm text-gray-600">Badan Hukum: {cooperative.legal_entity_number}</p>
                    <p className="text-sm text-gray-600">NPWP: {cooperative.npwp}</p>
                    <p className="text-sm text-gray-600">Alamat: {cooperative.address}</p> */}
                    <h3 className="text-lg font-semibold mt-4">Laporan Laba Rugi</h3>
                    <p className="text-sm text-gray-600">
                      Periode: {dayjs(filters.startDate).format('DD MMMM YYYY')} s/d {dayjs(filters.endDate).format('DD MMMM YYYY')}
                    </p>
                  </div>
                )}

                {/* Summary Cards */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6 no-print">
                  <div className="bg-green-50 border border-green-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-green-100 p-3 rounded-full">
                        <i className="mgc_currency_dollar_2_line text-green-600 text-2xl"></i>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Pendapatan</p>
                        <p className="text-xl font-bold text-green-700">
                          {new Intl.NumberFormat('id-ID', {
                            style: 'currency',
                            currency: 'IDR',
                            minimumFractionDigits: 0,
                          }).format(report?.revenue?.total || 0)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                    <div className="flex items-center gap-3">
                      <div className="bg-red-100 p-3 rounded-full">
                        <i className="mgc_invoice_line text-red-600 text-2xl"></i>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">Total Beban</p>
                        <p className="text-xl font-bold text-red-700">
                          {new Intl.NumberFormat('id-ID', {
                            style: 'currency',
                            currency: 'IDR',
                            minimumFractionDigits: 0,
                          }).format(report?.expenses?.total || 0)}
                        </p>
                      </div>
                    </div>
                  </div>

                  <div className={`${report?.net_income >= 0 ? 'bg-blue-50 border-blue-200' : 'bg-orange-50 border-orange-200'} border rounded-lg p-4`}>
                    <div className="flex items-center gap-3">
                      <div className={`${report?.net_income >= 0 ? 'bg-blue-100' : 'bg-orange-100'} p-3 rounded-full`}>
                        <i className={`mgc_trending_${report?.net_income >= 0 ? 'up' : 'down'}_line ${report?.net_income >= 0 ? 'text-blue-600' : 'text-orange-600'} text-2xl`}></i>
                      </div>
                      <div>
                        <p className="text-sm text-gray-600">
                          {report?.net_income >= 0 ? 'Laba Bersih' : 'Rugi Bersih'}
                        </p>
                        <p className={`text-xl font-bold ${report?.net_income >= 0 ? 'text-blue-700' : 'text-orange-700'}`}>
                          {new Intl.NumberFormat('id-ID', {
                            style: 'currency',
                            currency: 'IDR',
                            minimumFractionDigits: 0,
                          }).format(Math.abs(report?.net_income || 0))}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Table */}
                <IncomeStatementTable data={report} />
              </>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default IncomeStatement;
